/**
 * 全局游戏API，供WebMCP工具调用
 */

// 多语言：i18n.js 提供 window.I18N.t(key)，双语字典在 docs/index.html 的 I18N_DICT 中
const I18N = (window as any).I18N;
function t(key: string, params?: Record<string, string | number>): string {
  let s = I18N && typeof I18N.t === 'function' ? I18N.t(key) : key;
  if (params) {
    Object.keys(params).forEach(k => { s = s.replace('{' + k + '}', String(params[k])); });
  }
  return s;
}

// 游戏状态存储
let gameState = {
  matrix: [[0,0,0,0],[0,0,0,0],[0,0,0,0],[0,0,0,0]],
  currentScore: 0,
  highScore: 0,
  isGameOver: false
};

// 移动回调函数（返回游戏是否结束 + 棋盘是否发生变化）
export type MoveResult = { gameOver: boolean; changed: boolean };
let moveCallback: ((direction: string) => MoveResult) | null = null;
let restartCallback: (() => void) | null = null;

/**
 * 更新游戏状态（由React组件调用）
 */
export function updateGameState(newState: Partial<typeof gameState>) {
  gameState = { ...gameState, ...newState };
}

/**
 * 设置移动回调（由React组件调用）
 */
export function setMoveCallback(callback: (direction: string) => MoveResult) {
  moveCallback = callback;
}

/**
 * 设置重启回调（由React组件调用）
 */
export function setRestartCallback(callback: () => void) {
  restartCallback = callback;
}

/**
 * 获取游戏状态
 */
export function getState() {
  return {
    matrix: gameState.matrix,
    currentScore: gameState.currentScore,
    highScore: gameState.highScore,
    isGameOver: gameState.isGameOver,
    gridSize: 4
  };
}

/**
 * 移动方块
 */
export function move(direction: string): { success: boolean; reason?: string; message: string } {
  if (!['up', 'down', 'left', 'right'].includes(direction)) {
    return { success: false, message: t('g3072_invalid_dir', { d: direction }) };
  }

  if (gameState.isGameOver) {
    return { success: false, message: t('g3072_game_over') };
  }

  if (moveCallback) {
    const { gameOver, changed } = moveCallback(direction);
    if (gameOver) {
      updateGameState({ isGameOver: true });
    }
    if (!changed) {
      return { success: false, reason: 'no-op', message: t('g3072_noop') };
    }
    return { success: true, message: t('g3072_moved', { d: direction }) };
  }

  return { success: false, message: t('g3072_not_init') };
}

/**
 * 重新开始游戏
 */
export function restart(): { success: boolean; message: string } {
  if (restartCallback) {
    restartCallback();
    updateGameState({ isGameOver: false, currentScore: 0 });
    return { success: true, message: t('g3072_restarted') };
  }
  return { success: false, message: t('g3072_not_init') };
}

/**
 * 初始化WebMCP接口
 */
export function initWebMCP() {
  if (typeof window === 'undefined') return;

  // 检查是否已经初始化
  if ((window as any).webmcpReady) {
    console.log('[3072] WebMCP 已初始化，跳过重复注册');
    return;
  }

  // 暴露到全局
  (window as any).gameAPI = {
    getState,
    move,
    restart
  };

  // 检查 navigator.modelContext 是否存在
  if (!(navigator as any).modelContext) {
    console.log('[3072] navigator.modelContext 不可用，WebMCP 未注册');
    return;
  }

  const mc = (navigator as any).modelContext;

  // 注册工具（语言切换时按新语言重新注册）
  function registerTools(target: any) {
    target.registerTool({
      name: 'game3072_get_state',
      description: t('g3072_get_state_desc'),
      inputSchema: {
        type: 'object',
        properties: {
          format: {
            type: 'string',
            enum: ['json', 'text'],
            description: t('g3072_format_desc')
          }
        }
      },
      execute: (args: { format?: string }) => {
        const state = getState();
        if (args.format === 'text') {
          let text = `${t('g3072_score_cur')}: ${state.currentScore}\n${t('g3072_score_high')}: ${state.highScore}\n${t('g3072_status')}: ${state.isGameOver ? t('g3072_over') : t('g3072_playing')}\n\n${t('g3072_grid')}:\n`;
          state.matrix.forEach(row => {
            text += row.map(v => v.toString().padStart(5)).join(' ') + '\n';
          });
          return { content: [{ type: 'text', text }] };
        }
        return { content: [{ type: 'text', text: JSON.stringify(state, null, 2) }] };
      }
    });

    target.registerTool({
      name: 'game3072_move',
      description: t('g3072_move_desc'),
      inputSchema: {
        type: 'object',
        properties: {
          direction: {
            type: 'string',
            enum: ['up', 'down', 'left', 'right'],
            description: t('g3072_dir_desc')
          }
        },
        required: ['direction']
      },
      execute: (args: { direction: string }) => {
        const result = move(args.direction);
        return { content: [{ type: 'text', text: JSON.stringify(result) }] };
      }
    });

    target.registerTool({
      name: 'game3072_restart',
      description: t('g3072_restart_desc'),
      inputSchema: {
        type: 'object',
        properties: {}
      },
      execute: () => {
        const result = restart();
        return { content: [{ type: 'text', text: JSON.stringify(result) }] };
      }
    });

    target.registerTool({
      name: 'game3072_execute_sequence',
      description: t('g3072_seq_desc'),
      inputSchema: {
        type: 'object',
        properties: {
          moves: {
            type: 'array',
            items: {
              type: 'string',
              enum: ['up', 'down', 'left', 'right']
            },
            description: t('g3072_moves_desc')
          }
        },
        required: ['moves']
      },
      execute: async (args: { moves: string[] }) => {
        const results: Array<{ direction: string; success: boolean; reason?: string; message: string }> = [];
        for (const direction of args.moves) {
          const result = move(direction);
          results.push({ direction, ...result });
          if (!result.success) break;
          // H10: 每步落地后让出事件循环，等待 React 提交新盘面并重注册 moveCallback
          // （回调闭包捕获的是最新的 gameMatrix）。否则整段序列都基于同一份旧棋盘
          // 计算，finalState 与逐单步移动不一致（静默脏数据）。
          await new Promise(resolve => setTimeout(resolve, 0));
        }
        return { content: [{ type: 'text', text: JSON.stringify({ results, finalState: getState() }) }] };
      }
    });
  }

  registerTools(mc);

  // 语言切换（i18n.js setLang() 会 dispatch games-i18n-change）→ 按新语言重新注册工具
  const TOOL_NAMES = ['game3072_get_state', 'game3072_move', 'game3072_restart', 'game3072_execute_sequence'];
  window.addEventListener('games-i18n-change', () => {
    try {
      TOOL_NAMES.forEach((n) => { try { mc.unregisterTool(n); } catch (e) {} });
      registerTools(mc);
    } catch (e) { console.warn('[3072] 语言切换重注册失败', e); }
  });

  console.log('[3072] WebMCP 工具已注册');

  // 标记就绪
  (window as any).webmcpReady = true;

  // webmcp-a11y-expose.js 会自动暴露工具列表到无障碍树
}
